// A strip of time on one machine: production operations from BC in gray, maintenance hours shaded off, the
// maintenance window in the brand color (outlined red when it runs into production) and a suggested opening dashed.
import { plant, type Hours, type Op } from "./mxApi";

const DAY = 864e5;

export function Timeline({ from, to, ops, window, slot, hours, clash, onPickOp }: {
  from: number; to: number; ops: Op[]; window?: { start: number; end: number } | null; slot?: { start: number; end: number } | null; hours: Hours; clash?: boolean; onPickOp?: (o: Op) => void;
}) {
  const span = Math.max(1, to - from);
  const pct = (t: number) => `${Math.min(100, Math.max(0, ((t - from) / span) * 100))}%`;
  const width = (s: number, e: number) => `${Math.max(0, ((Math.min(e, to) - Math.max(s, from)) / span) * 100)}%`;
  // Day boundaries in the plant's time zone, for labels.
  const days: { t: number; label: string }[] = [];
  for (let t = Math.floor(from / DAY) * DAY; t < to + DAY; t += DAY) {
    const mid = t + 12 * 36e5;
    const label = plant(new Date(mid).toISOString(), hours.tz, { weekday: "short", day: "numeric" });
    const local = new Date(mid).toLocaleString("en-US", { timeZone: hours.tz, hour12: false, hour: "2-digit" });
    const start = mid - (Number(local) % 24) * 36e5;
    if (start >= from - DAY && start < to) days.push({ t: Math.max(start, from), label });
  }
  const now = Date.now();
  return (
    <div>
      <div className="mx-tl-days" aria-hidden="true">{days.map((d) => <span key={d.t} style={{ left: pct(d.t) }}>{d.label}</span>)}</div>
      <div className="mx-tl" role="img" aria-label={`${ops.length} production operations${window ? ", the maintenance window" : ""}`}>
        {ops.map((o) => {
          const s = Date.parse(o.start), e = Date.parse(o.end);
          return <span key={`${o.prodOrderNo}-${o.operationNo}-${o.start}`} className="op" style={{ left: pct(s), width: width(s, e), cursor: onPickOp ? "pointer" : undefined }}
            title={`${o.prodOrderNo} op ${o.operationNo}: ${o.description}${o.itemNo ? ` (${o.itemNo})` : ""}\n${plant(o.start, hours.tz)} – ${plant(o.end, hours.tz, { hour: "numeric", minute: "2-digit" })}\n${o.status.replace(/_x0020_/g, " ")}`}
            onClick={onPickOp ? () => onPickOp(o) : undefined} />;
        })}
        {slot && <span className="win slot" style={{ left: pct(slot.start), width: width(slot.start, slot.end) }} title="Suggested opening" />}
        {window && <span className={`win ${clash ? "clash" : ""}`} style={{ left: pct(window.start), width: width(window.start, window.end) }} title="Maintenance window" />}
        {now > from && now < to && <span className="now" style={{ left: pct(now) }} title="Now" />}
      </div>
      <div className="mx-tl-legend">
        <span><i style={{ background: "#C8C8C8" }} />Production in BC</span>
        <span><i style={{ background: "var(--brand)" }} />Maintenance window</span>
        {slot && <span><i style={{ border: "2px dashed var(--brand)", width: 10, height: 8 }} />Suggested</span>}
        {clash && <span><i style={{ background: "var(--brand)", boxShadow: "0 0 0 2px var(--danger)" }} />Runs into production</span>}
      </div>
    </div>
  );
}
